'use client';

import { usePathname } from 'next/navigation';
import { createContext, useContext, useEffect, useLayoutEffect } from 'react';

export type CoordinatorBreadcrumbItem = {
  label: string;
  href?: string;
};

type CoordinatorShellContextValue = {
  registerBreadcrumbs: (pathname: string, items: CoordinatorBreadcrumbItem[]) => void;
  registerNavigationGuard: (pathname: string, guard: (proceed: () => void) => void) => () => void;
};

const CoordinatorShellContext = createContext<CoordinatorShellContextValue | null>(null);

export const CoordinatorShellProvider = CoordinatorShellContext.Provider;

export const useCoordinatorShell = () => useContext(CoordinatorShellContext);

export const useCoordinatorBreadcrumbs = (items: CoordinatorBreadcrumbItem[]) => {
  const shell = useCoordinatorShell();
  const pathname = usePathname();
  const itemKey = items.map(({ href, label }) => `${label}:${href ?? ''}`).join('|');

  // Call sites intentionally pass small inline arrays. The value key makes the
  // registration semantic, rather than retriggering it for each array identity.
  //
  // COM-861 — a layout effect, not a passive one: registration sets state in the shell, and a
  // passive effect let the fallback label (first URL segment) paint for a frame before the real
  // crumbs replaced it — a visible flicker and an extra shell render on every client navigation.
  // State set in a layout effect is flushed before paint. A cold server render still shows the
  // fallback first; that path is unchanged.
  // biome-ignore lint/correctness/useExhaustiveDependencies: itemKey tracks the complete item value
  useLayoutEffect(() => {
    if (!shell || items.length === 0) return;
    shell.registerBreadcrumbs(pathname, items);
  }, [itemKey, pathname, shell]);

  return shell != null;
};

export const useCoordinatorNavigationGuard = (guard: (proceed: () => void) => void) => {
  const shell = useCoordinatorShell();
  const pathname = usePathname();

  useEffect(() => {
    if (!shell) return;
    return shell.registerNavigationGuard(pathname, guard);
  }, [guard, pathname, shell]);
};
